import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import { AmplitudeAnalyticsContext } from '../../../../contexts/useAnalytics';
import { updateSelectedProviders } from '../../../../redux/appBuilder/stepperDetails';
import {
  getAppActiveStep,
  getSelectedProviders,
} from '../../../../redux/appBuilder/stepperDetails/selectors';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { HttpsProvider } from '../../../../redux/httpsProviders/types';
import { resetNetworkStore } from '../../../../redux/providerBuilder/networkRequestStore';
import { resetProviderDetails } from '../../../../redux/providerBuilder/providerDetails';
import { getProviderDetails } from '../../../../redux/providerBuilder/providerDetails/selectors';
import { resetSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances';
import { resetSelectedNetworkRequest } from '../../../../redux/providerBuilder/selectedNetworkRequest';
import {
  resetActiveStep,
  updateActiveStep,
  updateBackAction,
} from '../../../../redux/providerBuilder/stepperDetails';
import { getActiveStep } from '../../../../redux/providerBuilder/stepperDetails/selectors';
import { sendDevtoolLogs } from '../../../../utils/logs/send-logs';
import { getProviderById } from '../../../../utils/provider-builder/register-provider-utils';
import { getFirebaseIdToken } from '../../../../utils/utils';
import { VerifyProvider } from './verify-provider.component';

export const ReviewProvider = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const providerDetails = useAppSelector(getProviderDetails);
  const selectedProvidersFromCreateApp = useAppSelector(getSelectedProviders);

  const activeStep = useAppSelector(getActiveStep);

  const appActiveStep = useAppSelector(getAppActiveStep);

  const [providerDetailsFromBackend, setProviderDetailsFromBackend] =
    useState<HttpsProvider>();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [providerVerified, setProviderVerified] = useState(providerDetails.isActive);
  const cancelRef = useRef<HTMLInputElement>(null);

  const { logEvent } = useContext(AmplitudeAnalyticsContext);

  useEffect(() => {
    // Focus the button when the component mounts
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const fetchProvider = async () => {
      if (providerVerified) {
        const accessToken = (await getFirebaseIdToken()) ?? '';
        const provider = await getProviderById(
          accessToken,
          providerDetails.httpProviderId
        );
        setProviderDetailsFromBackend(provider);
      }
    };

    fetchProvider();
  }, [providerVerified]);

  function handleEditProvider() {
    dispatch(updateBackAction(true));
    dispatch(updateActiveStep(activeStep - 1));
  }

  async function handleFinish() {
    setButtonLoading(true);
    if (providerVerified) {
      await sendDevtoolLogs('PROVIDER_PUBLISHED', providerDetails.httpProviderId);
      logEvent('PROVIDER_CREATED');
    }

    // Clear all suggestions from sessionStorage
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('suggestions_')) {
        sessionStorage.removeItem(key);
      }
    });

    dispatch(resetNetworkStore());
    dispatch(resetProviderDetails());
    dispatch(resetSelectedInstances());
    dispatch(resetSelectedNetworkRequest());
    dispatch(resetActiveStep());

    if (appActiveStep === 2) {
      dispatch(
        updateSelectedProviders([
          ...selectedProvidersFromCreateApp,
          providerDetailsFromBackend,
        ])
      );
      navigate('/new-application');
      return;
    }

    navigate('/my-providers');
  }

  return (
    <>
      <Box>
        <Flex
          direction="column"
          alignItems="center"
          p={10}
          pb={0}
          gap={5}
          h="calc(100vh - 240px)"
          overflowY="auto"
        >
          {!providerVerified ? (
            <>
              <Heading fontSize="xx-large" textAlign="center">
                Test the provider
              </Heading>
              <Box>
                <VerifyProvider
                  httpProviderId={providerDetails?.httpProviderId}
                  setProviderVerified={setProviderVerified}
                />
              </Box>
              <Text color="gray" fontSize="small" textAlign="center">
                Scan this QR code to test the provider.
                <br />
                You can publish only after a successful test.
              </Text>
            </>
          ) : (
            <>
              <Heading fontSize="xx-large" textAlign="center">
                Test successful!
              </Heading>
              <img
                src="/assets/test-successful.png"
                alt="test-successful"
                width={300}
                height={300}
              />
              <Box marginBottom="10vh" />
            </>
          )}
        </Flex>
        <Box w="full" mt={10} backgroundColor="gray.50" px={30} py={4}>
          <Flex w="full" justifyContent="space-between">
            <Button
              // onClick={handleGoBack}
              backgroundColor="transparent"
              border="1px solid gray"
              borderRadius={10}
              fontWeight="normal"
              _hover={{
                fontWeight: 'bold',
                transform: 'scale(1.05)',
              }}
              isLoading={buttonLoading}
              onClick={handleEditProvider}
            >
              Edit
            </Button>
            {!providerVerified ? (
              <Flex alignItems="center" gap={3}>
                <Text color="gray.500" fontSize="md">
                  6/7
                </Text>
                <Button
                  backgroundColor="black"
                  color="white"
                  border="1px solid black"
                  borderRadius={10}
                  fontWeight="normal"
                  _hover={{
                    transform: 'scale(1.05)',
                  }}
                  onClick={async () => {
                    if (providerVerified) {
                      await handleFinish();
                    }
                  }}
                  isLoading={buttonLoading}
                  isDisabled={!providerVerified}
                >
                  Next
                </Button>
              </Flex>
            ) : (
              <Flex alignItems="center" gap={3}>
                <Text color="gray.500" fontSize="md">
                  7/7
                </Text>
                <Button
                  backgroundColor="black"
                  color="white"
                  border="1px solid black"
                  borderRadius={10}
                  fontWeight="normal"
                  _hover={{
                    transform: 'scale(1.05)',
                  }}
                  onClick={handleFinish}
                  isLoading={buttonLoading}
                >
                  Publish
                </Button>
              </Flex>
            )}
          </Flex>
        </Box>
      </Box>
    </>
  );
};

